import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Share, Calendar, BarChart3, Users, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import waitlistService from '../services/waitlistService';
import { toast } from 'react-hot-toast';

const ComingSoonPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const upcomingFeatures = [
    {
      id: 'social_publishing',
      name: 'Social Publishing',
      description: 'Publish content directly to Facebook, Instagram, X, and LinkedIn',
      icon: Share,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'content_scheduling',
      name: 'Content Scheduling',
      description: 'Schedule posts in advance with our smart calendar system',
      icon: Calendar,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'social_analytics',
      name: 'Social Analytics',
      description: 'Track engagement, reach, and performance across all platforms',
      icon: BarChart3,
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'team_collaboration',
      name: 'Enhanced Team Features',
      description: 'Advanced collaboration tools for agencies and teams',
      icon: Users,
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 'automation',
      name: 'Smart Automation',
      description: 'AI-powered posting schedules and content optimization',
      icon: Zap,
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  const handleFeatureToggle = (featureId: string) => {
    setSelectedFeatures(prev => 
      prev.includes(featureId) 
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await waitlistService.joinSocialFeaturesWaitlist(email, selectedFeatures);
      setIsSubmitted(true);
      setEmail('');
      toast.success('Successfully joined the waitlist! We\'ll notify you when these features are ready.');
    } catch (error) {
      console.error('Failed to join waitlist:', error);
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)', boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)' }}>
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Amazing Features Coming Soon!
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              We're working hard to bring you the complete social media management experience. 
              Join our waitlist to be the first to know when these features launch.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {upcomingFeatures.map((feature) => (
              <Card 
                key={feature.id}
                className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  selectedFeatures.includes(feature.id) 
                    ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                    : 'hover:shadow-md'
                }`}
                onClick={() => handleFeatureToggle(feature.id)}
              >
                <CardHeader className="pb-3">
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center mb-3`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">{feature.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm">
                    {feature.description}
                  </CardDescription>
                  {selectedFeatures.includes(feature.id) && (
                    <div className="mt-3 flex items-center gap-2 text-blue-600 dark:text-blue-400">
                      <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 8 8">
                          <path d="M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z"/>
                        </svg>
                      </div>
                      <span className="text-sm font-medium">Selected</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Waitlist Form */}
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle>Join the Waitlist</CardTitle>
              <CardDescription>
                {selectedFeatures.length > 0 
                  ? `Get notified when ${selectedFeatures.length} selected feature${selectedFeatures.length > 1 ? 's are' : ' is'} ready`
                  : 'Get notified when these features are ready'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!isSubmitted ? (
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <div>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full text-white"
                    style={{ 
                      background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                      backgroundSize: '200% 200%',
                      backgroundPosition: 'left center',
                      boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.currentTarget.style.backgroundPosition = 'right center';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundPosition = 'left center';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Joining...
                      </>
                    ) : (
                      'Join Waitlist'
                    )}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    You're on the list!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    We'll notify you as soon as these features are ready.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <div className="mt-12 text-center">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Expected Timeline
            </h3>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 text-blue-700 dark:text-blue-300">
                <Calendar className="w-5 h-5" />
                <span className="font-semibold">Coming in Q1 2025</span>
              </div>
              <p className="text-blue-600 dark:text-blue-400 text-sm mt-2">
                We're currently working on business verification and Meta app approval to bring you these social features.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage;